import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Gift, Plus, Trash2, Edit2, Save, X, Sparkles } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { Footer, FamilySwitcher } from './Layout';
import SmartAddSheet from './smartAdd/SmartAddSheet';
import { addGiftsBulk, removeGiftsByIds, newGiftIds } from './smartAdd/giftsStore';
import { normalizeUrl, safeHref, INVALID_LINK_MESSAGE } from './smartAdd/giftText';

export default function FamilyApp({ code, family, gifts, profiles, currentUser, onLogout }) {
  const familyMembers = family.members;
  const [newGiftTitle, setNewGiftTitle] = useState('');
  const [newGiftLink, setNewGiftLink] = useState('');
  const [newGiftNotes, setNewGiftNotes] = useState('');
  const [showAddGift, setShowAddGift] = useState(false);
  const [activeTab, setActiveTab] = useState('my-list');
  const [editingGiftId, setEditingGiftId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editLink, setEditLink] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showGiftedConfirm, setShowGiftedConfirm] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [tempProfile, setTempProfile] = useState({});
  const [editingSizes, setEditingSizes] = useState(false);
  const [tempSizes, setTempSizes] = useState({});
  
  // New state for custom gifts
  const [addingCustomGiftFor, setAddingCustomGiftFor] = useState(null);
  const [customGiftTitle, setCustomGiftTitle] = useState('');
  const [customGiftDescription, setCustomGiftDescription] = useState('');
  const [customGiftLink, setCustomGiftLink] = useState('');

  // Link validation errors for the manual forms (shown under the field)
  const [linkError, setLinkError] = useState('');

  // Smart Add: which list the sheet is open for (a member name), the new-card highlight, and the undo toast
  const [smartAddFor, setSmartAddFor] = useState(null);
  const [freshIds, setFreshIds] = useState([]);
  const [toast, setToast] = useState(null); // { message, undo?: { person, ids } }
  const freshTimer = useRef(null);
  const toastTimer = useRef(null);
  useEffect(() => () => { clearTimeout(freshTimer.current); clearTimeout(toastTimer.current); }, []);


  // Per-member writes: merge only that member's list so concurrent edits to
  // different members don't overwrite each other.
  const giftsRef = doc(db, 'families', code, 'data', 'gifts');
  const profilesRef = doc(db, 'families', code, 'data', 'profiles');

  const saveMemberGifts = async (person, list) => {
    try {
      await setDoc(giftsRef, { gifts: { [person]: list } }, { merge: true });
    } catch (error) {
      console.error('Failed to save data:', error);
    }
  };

  const addGift = async () => {
    if (!newGiftTitle || !currentUser) return;
    const link = normalizeUrl(newGiftLink);
    if (link === null) { setLinkError(INVALID_LINK_MESSAGE); return; }

    const newGift = {
      id: Date.now(),
      title: newGiftTitle,
      link,
      notes: newGiftNotes,
      claimedBy: null,
      status: 'active',
      isCustomGift: false
    };

    await saveMemberGifts(currentUser, [...(gifts[currentUser] || []), newGift]);

    setNewGiftTitle('');
    setNewGiftLink('');
    setNewGiftNotes('');
    setLinkError('');
    setShowAddGift(false);
  };

  const addCustomGift = async (forPerson) => {
    if (!customGiftTitle || !currentUser) return;
    const link = normalizeUrl(customGiftLink);
    if (link === null) { setLinkError(INVALID_LINK_MESSAGE); return; }

    const newCustomGift = {
      id: Date.now(),
      title: customGiftTitle,
      link,
      notes: customGiftDescription,
      claimedBy: currentUser, // Already "claimed" by the creator
      status: 'active',
      isCustomGift: true,
      addedBy: currentUser
    };

    await saveMemberGifts(forPerson, [...(gifts[forPerson] || []), newCustomGift]);

    setCustomGiftTitle('');
    setCustomGiftDescription('');
    setCustomGiftLink('');
    setLinkError('');
    setAddingCustomGiftFor(null);
  };

  const deleteGift = (person, giftId) => {
    saveMemberGifts(person, (gifts[person] || []).filter(g => g.id !== giftId));
    setShowDeleteConfirm(null);
  };

  const markAsGifted = (person, giftId) => {
    saveMemberGifts(person, (gifts[person] || []).map(g =>
      g.id === giftId
        ? { ...g, status: 'gifted', giftedDate: new Date().toISOString() }
        : g
    ));
    setShowGiftedConfirm(null);
  };

  const startEdit = (gift) => {
    setEditingGiftId(gift.id);
    setEditTitle(gift.title);
    setEditLink(gift.link || '');
    setEditNotes(gift.notes || '');
  };

  const cancelEdit = () => {
    setLinkError('');
    setEditingGiftId(null);
    setEditTitle('');
    setEditLink('');
    setEditNotes('');
  };

  const saveEdit = (person) => {
    const link = normalizeUrl(editLink);
    if (link === null) { setLinkError(INVALID_LINK_MESSAGE); return; }
    saveMemberGifts(person, (gifts[person] || []).map(g =>
      g.id === editingGiftId
        ? { ...g, title: editTitle, link, notes: editNotes }
        : g
    ));
    cancelEdit();
  };

  const toggleClaim = (person, giftId) => {
    saveMemberGifts(person, (gifts[person] || []).map(g =>
      g.id === giftId
        ? { ...g, claimedBy: g.claimedBy === currentUser ? null : currentUser }
        : g
    ));
  };

  const showToast = (message, undo) => {
    clearTimeout(toastTimer.current);
    setToast({ message, undo });
    toastTimer.current = setTimeout(() => setToast(null), undo ? 9000 : 3500);
  };

  // Saves every Smart Add row in one transaction, then highlights the new cards and offers Undo.
  const saveSmartAdd = async (person, rows) => {
    const own = person === currentUser;
    const ids = newGiftIds(rows.length);
    const newGifts = rows.map((r, i) => ({
      id: ids[i],
      title: r.title,
      link: r.link,
      notes: r.notes,
      claimedBy: r.claim ? currentUser : null,
      status: 'active',
      isCustomGift: !own,
      ...(own ? {} : { addedBy: currentUser })
    }));
    await addGiftsBulk(db, code, person, newGifts);
    setSmartAddFor(null);
    setFreshIds(ids);
    clearTimeout(freshTimer.current);
    freshTimer.current = setTimeout(() => setFreshIds([]), 6000);
    const claimed = newGifts.filter(g => g.claimedBy).length;
    showToast(
      `Added ${ids.length} ${ids.length === 1 ? 'gift' : 'gifts'} to ${own ? 'your wishlist' : `${person}’s list`}${claimed ? `, ${claimed} claimed by you` : ''}.`,
      { person, ids }
    );
    setTimeout(() => {
      const card = document.querySelector('[data-fresh="true"]');
      if (card && card.scrollIntoView) card.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 50);
  };

  const undoSmartAdd = async ({ person, ids }) => {
    try {
      await removeGiftsByIds(db, code, person, ids);
      setFreshIds([]);
      showToast('Undone. Nothing was added.');
    } catch (error) {
      console.error('Undo failed:', error);
      showToast('Couldn’t undo that. Please try again.');
    }
  };

  const closeSmartAdd = useCallback(() => setSmartAddFor(null), []);

  const freshClass = (id) => (freshIds.includes(id) ? 'gift-fresh' : '');
  const newChip = (id) => (freshIds.includes(id)
    ? <span className="ml-2 px-2 py-0.5 rounded-full bg-green-600 text-white text-xs font-bold align-middle">New</span>
    : null);

  const startEditProfile = () => {
    setEditingProfile(true);
    setTempProfile({
      favoriteColors: profiles[currentUser]?.favoriteColors || '',
      favoriteCandies: profiles[currentUser]?.favoriteCandies || '',
      favoriteFlavors: profiles[currentUser]?.favoriteFlavors || ''
    });
  };

  const saveProfile = async () => {
    await setDoc(profilesRef, { profiles: { [currentUser]: { ...(profiles[currentUser] || {}), ...tempProfile } } }, { merge: true });
    setEditingProfile(false);
    setTempProfile({});
  };

  const cancelProfileEdit = () => {
    setEditingProfile(false);
    setTempProfile({});
  };

  const startEditSizes = () => {
    setEditingSizes(true);
    setTempSizes({
      shirts: profiles[currentUser]?.shirts || '',
      sweatshirts: profiles[currentUser]?.sweatshirts || '',
      slacks: profiles[currentUser]?.slacks || '',
      pajamas: profiles[currentUser]?.pajamas || '',
      underwear: profiles[currentUser]?.underwear || '',
      shoeSize: profiles[currentUser]?.shoeSize || '',
      dress: profiles[currentUser]?.dress || '',
      blouses: profiles[currentUser]?.blouses || '',
      jacket: profiles[currentUser]?.jacket || '',
      diapers: profiles[currentUser]?.diapers || ''
    });
  };

  const saveSizes = async () => {
    await setDoc(profilesRef, { profiles: { [currentUser]: { ...(profiles[currentUser] || {}), ...tempSizes } } }, { merge: true });
    setEditingSizes(false);
    setTempSizes({});
  };

  const cancelSizesEdit = () => {
    setEditingSizes(false);
    setTempSizes({});
  };

  return (
    <div className="min-h-screen p-8" style={{
          backgroundColor: '#1a472a',
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23228B22' fill-opacity='0.4'%3E%3Crect x='35' y='10' width='10' height='10'/%3E%3Crect x='30' y='20' width='20' height='10'/%3E%3Crect x='25' y='30' width='30' height='10'/%3E%3Crect x='20' y='40' width='40' height='10'/%3E%3Crect x='35' y='50' width='10' height='10'/%3E%3C/g%3E%3Cg fill='%23DC143C' fill-opacity='0.3'%3E%3Crect x='5' y='5' width='5' height='5'/%3E%3Crect x='70' y='15' width='5' height='5'/%3E%3Crect x='10' y='65' width='5' height='5'/%3E%3C/g%3E%3Cg fill='%23FFD700' fill-opacity='0.5'%3E%3Crect x='35' y='5' width='10' height='5'/%3E%3C/g%3E%3C/svg%3E"),
            repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(255, 255, 255, 0.02) 4px, rgba(255, 255, 255, 0.02) 8px)
          `,
          backgroundSize: '80px 80px, 8px 8px'
        }}>
          <div className="fixed inset-0 pointer-events-none" style={{
            backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(255, 255, 255, 0.02) 4px, rgba(255, 255, 255, 0.02) 8px)',
            backgroundSize: '8px 8px'
          }}></div>
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FamilySwitcher currentCode={code}><Gift className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 flex-shrink-0" /></FamilySwitcher>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">
                Welcome, {currentUser}!
              </h1>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base whitespace-nowrap flex-shrink-0"
            >
              Log Out
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('my-list')}
              className={`flex-1 min-w-[140px] px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'my-list'
                  ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              🎁 My Wishlist
            </button>
            <button
              onClick={() => setActiveTab('others')}
              className={`flex-1 min-w-[140px] px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'others'
                  ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              🎅 Others' Wishlists
            </button>
          </div>

          <div className="p-4 sm:p-6">
            {activeTab === 'my-list' ? (
              <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-lg border-2 border-gray-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800">My Favorites</h2>
                    {!editingProfile && (
                      <button
                        onClick={startEditProfile}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                  </div>
                  
                  {editingProfile ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Favorite Colors
                        </label>
                        <input
                          type="text"
                          value={tempProfile.favoriteColors}
                          onChange={(e) => setTempProfile({...tempProfile, favoriteColors: e.target.value})}
                          placeholder="e.g., Blue, Green"
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Favorite Candies
                        </label>
                        <input
                          type="text"
                          value={tempProfile.favoriteCandies}
                          onChange={(e) => setTempProfile({...tempProfile, favoriteCandies: e.target.value})}
                          placeholder="e.g., Reese's, Snickers"
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Favorite Flavors
                        </label>
                        <input
                          type="text"
                          value={tempProfile.favoriteFlavors}
                          onChange={(e) => setTempProfile({...tempProfile, favoriteFlavors: e.target.value})}
                          placeholder="e.g., Vanilla, Chocolate"
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={saveProfile}
                          className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center gap-1"
                        >
                          <Save className="w-4 h-4" />
                          Save
                        </button>
                        <button
                          onClick={cancelProfileEdit}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {profiles[currentUser]?.favoriteColors ? (
                        <p className="text-sm"><span className="font-medium text-gray-700">Colors:</span> <span className="text-gray-600">{profiles[currentUser].favoriteColors}</span></p>
                      ) : (
                        <p className="text-sm text-gray-400">Colors: Not set</p>
                      )}
                      {profiles[currentUser]?.favoriteCandies ? (
                        <p className="text-sm"><span className="font-medium text-gray-700">Candies:</span> <span className="text-gray-600">{profiles[currentUser].favoriteCandies}</span></p>
                      ) : (
                        <p className="text-sm text-gray-400">Candies: Not set</p>
                      )}
                      {profiles[currentUser]?.favoriteFlavors ? (
                        <p className="text-sm"><span className="font-medium text-gray-700">Flavors:</span> <span className="text-gray-600">{profiles[currentUser].favoriteFlavors}</span></p>
                      ) : (
                        <p className="text-sm text-gray-400">Flavors: Not set</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg border-2 border-gray-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800">My Sizes</h2>
                    {!editingSizes && (
                      <button
                        onClick={startEditSizes}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                  </div>
                  
                  {editingSizes ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { key: 'shirts', label: 'Shirts', placeholder: 'e.g., M, L' },
                          { key: 'sweatshirts', label: 'Sweatshirts / Sweaters', placeholder: 'e.g., L, XL' },
                          { key: 'slacks', label: 'Slacks / Jeans', placeholder: 'e.g., 32x30, 8' },
                          { key: 'pajamas', label: 'Pajamas', placeholder: 'e.g., M' },
                          { key: 'underwear', label: 'Underwear (Brand)', placeholder: 'e.g., M - Hanes' },
                          { key: 'shoeSize', label: 'Shoe Size', placeholder: 'e.g., 10, 9.5' },
                          { key: 'dress', label: 'Dress', placeholder: 'e.g., 8, M' },
                          { key: 'blouses', label: 'Blouses', placeholder: 'e.g., S, M' },
                          { key: 'jacket', label: 'Jacket', placeholder: 'e.g., L, 42R' },
                          { key: 'diapers', label: 'Diapers', placeholder: 'e.g., Size 4, Newborn' }
                        ].map(field => (
                          <div key={field.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label}
                            </label>
                            <input
                              type="text"
                              value={tempSizes[field.key]}
                              onChange={(e) => setTempSizes({...tempSizes, [field.key]: e.target.value})}
                              placeholder={field.placeholder}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={saveSizes}
                          className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center gap-1"
                        >
                          <Save className="w-4 h-4" />
                          Save
                        </button>
                        <button
                          onClick={cancelSizesEdit}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {[
                        { key: 'shirts', label: 'Shirts' },
                        { key: 'sweatshirts', label: 'Sweatshirts/Sweaters' },
                        { key: 'slacks', label: 'Slacks/Jeans' },
                        { key: 'pajamas', label: 'Pajamas' },
                        { key: 'underwear', label: 'Underwear' },
                        { key: 'shoeSize', label: 'Shoe Size' },
                        { key: 'dress', label: 'Dress' },
                        { key: 'blouses', label: 'Blouses' },
                        { key: 'jacket', label: 'Jacket' },
                        { key: 'diapers', label: 'Diapers' }
                      ].map(field => (
                        <div key={field.key}>
                          {profiles[currentUser]?.[field.key] ? (
                            <p className="text-sm"><span className="font-medium text-gray-700">{field.label}:</span> <span className="text-gray-600">{profiles[currentUser][field.key]}</span></p>
                          ) : (
                            <p className="text-sm text-gray-400">{field.label}: Not set</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
                  <h2 className="text-xl font-bold mb-4 text-gray-800">My Wishlist</h2>

                  <div className="mb-4">
                    {!showAddGift ? (
                      <div className="smart-box">
                        <div className="smart-actions">
                          <button
                            onClick={() => setShowAddGift(true)}
                            className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Add Manually
                          </button>
                          <button
                            onClick={() => setSmartAddFor(currentUser)}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                          >
                            <Sparkles className="w-4 h-4" />
                            Smart Add
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Gift title"
                          value={newGiftTitle}
                          onChange={(e) => setNewGiftTitle(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                        <input
                          type="text"
                          placeholder="Link (optional)"
                          value={newGiftLink}
                          onChange={(e) => { setNewGiftLink(e.target.value); setLinkError(''); }}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                        {linkError && showAddGift && <p className="text-sm text-red-600">{linkError}</p>}
                        <textarea
                          placeholder="Notes (optional) - Add details like size, color, specific version, etc."
                          value={newGiftNotes}
                          onChange={(e) => setNewGiftNotes(e.target.value)}
                          rows="3"
                          className="w-full p-2 border border-gray-300 rounded-lg resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={addGift}
                            className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                          >
                            Add
                          </button>
                          <button
                            onClick={() => {
                              setShowAddGift(false);
                              setNewGiftTitle('');
                              setNewGiftLink('');
                              setNewGiftNotes('');
                              setLinkError('');
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {(gifts[currentUser] || [])
                      .filter(g => g.status !== 'gifted' && !g.isCustomGift) // Filter out custom gifts from own wishlist
                      .map(gift => {
                      const isEditing = editingGiftId === gift.id;
                      const isDeleting = showDeleteConfirm === gift.id;
                      const isGiftedConfirm = showGiftedConfirm === gift.id;

                      return (
                        <div
                          key={gift.id}
                          data-fresh={freshIds.includes(gift.id)}
                          className={`p-3 border rounded-lg bg-gray-50 border-gray-200 ${freshClass(gift.id)}`}
                        >
                          {isEditing ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                placeholder="Gift title"
                              />
                              <input
                                type="text"
                                value={editLink}
                                onChange={(e) => { setEditLink(e.target.value); setLinkError(''); }}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                placeholder="Link (optional)"
                              />
                              {linkError && <p className="text-sm text-red-600">{linkError}</p>}
                              <textarea
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                rows="3"
                                className="w-full p-2 border border-gray-300 rounded-lg resize-none"
                                placeholder="Notes (optional)"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => saveEdit(currentUser)}
                                  className="flex-1 px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 flex items-center justify-center gap-1"
                                >
                                  <Save className="w-4 h-4" />
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 flex items-center gap-1"
                                >
                                  <X className="w-4 h-4" />
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : isGiftedConfirm ? (
                            <div>
                              <p className="font-medium text-gray-800 mb-3 text-sm sm:text-base">
                                Did you receive this gift? It will be removed from your wishlist.
                              </p>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  onClick={() => markAsGifted(currentUser, gift.id)}
                                  className="flex-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                                >
                                  Yes, I received it!
                                </button>
                                <button
                                  onClick={() => setShowGiftedConfirm(null)}
                                  className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : isDeleting ? (
                            <div>
                              <p className="font-medium text-gray-800 mb-3 text-sm sm:text-base">
                                Are you sure you want to delete? Someone may have already claimed this.
                              </p>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  onClick={() => deleteGift(currentUser, gift.id)}
                                  className="flex-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                                >
                                  Yes, Delete
                                </button>
                                <button
                                  onClick={() => setShowDeleteConfirm(null)}
                                  className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-800 break-words">
                                  {gift.title}{newChip(gift.id)}
                                </p>
                                {safeHref(gift.link) && (
                                  <a
                                    href={safeHref(gift.link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-500 hover:underline break-all"
                                  >
                                    View link
                                  </a>
                                )}
                                {gift.notes && (
                                  <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">
                                    {gift.notes}
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex gap-1">
                                <button
                                  onClick={() => startEdit(gift)}
                                  className="p-1 text-blue-500 hover:bg-blue-100 rounded"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setShowGiftedConfirm(gift.id)}
                                  className="p-1 text-green-500 hover:bg-green-100 rounded"
                                  title="Mark as Gifted"
                                >
                                  <Gift className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setShowDeleteConfirm(gift.id)}
                                  className="p-1 text-red-500 hover:bg-red-100 rounded"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {(!gifts[currentUser] || gifts[currentUser].filter(g => g.status !== 'gifted' && !g.isCustomGift).length === 0) && (
                      <p className="text-gray-400 text-center py-4">
                        Add your first gift idea!
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {familyMembers
                  .filter(person => person !== currentUser)
                  .map(person => {
                    // Separate regular gifts from custom gifts
                    const allGifts = (gifts[person] || []).filter(g => g.status !== 'gifted');
                    const regularGifts = allGifts.filter(g => !g.isCustomGift);
                    const customGifts = allGifts.filter(g => g.isCustomGift);
                    
                    return (
                    <div key={person} className="bg-white rounded-lg border-2 border-gray-200 p-4 md:p-6">
                      <h2 className="text-xl font-bold mb-4 text-gray-800">
                        {person}'s Wishlist
                      </h2>

                      {(profiles[person]?.favoriteColors || profiles[person]?.favoriteCandies || profiles[person]?.favoriteFlavors) && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">{person}'s Favorites</h3>
                          <div className="space-y-1 text-sm text-gray-600 break-words">
                            {profiles[person]?.favoriteColors && (
                              <p><span className="font-medium">Colors:</span> {profiles[person].favoriteColors}</p>
                            )}
                            {profiles[person]?.favoriteCandies && (
                              <p><span className="font-medium">Candies:</span> {profiles[person].favoriteCandies}</p>
                            )}
                            {profiles[person]?.favoriteFlavors && (
                              <p><span className="font-medium">Flavors:</span> {profiles[person].favoriteFlavors}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {(profiles[person]?.shirts || profiles[person]?.sweatshirts || profiles[person]?.slacks || 
                        profiles[person]?.pajamas || profiles[person]?.underwear || profiles[person]?.shoeSize || 
                        profiles[person]?.dress || profiles[person]?.blouses || profiles[person]?.jacket || profiles[person]?.diapers) && (
                        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <h3 className="text-sm font-semibold text-gray-700 mb-2">{person}'s Sizes</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                            {[
                              { key: 'shirts', label: 'Shirts' },
                              { key: 'sweatshirts', label: 'Sweatshirts' },
                              { key: 'slacks', label: 'Slacks/Jeans' },
                              { key: 'pajamas', label: 'Pajamas' },
                              { key: 'underwear', label: 'Underwear' },
                              { key: 'shoeSize', label: 'Shoe Size' },
                              { key: 'dress', label: 'Dress' },
                              { key: 'blouses', label: 'Blouses' },
                              { key: 'jacket', label: 'Jacket' },
                              { key: 'diapers', label: 'Diapers' }
                            ].map(field => (
                              profiles[person]?.[field.key] && (
                                <p key={field.key} className="break-words">
                                  <span className="font-medium">{field.label}:</span> {profiles[person][field.key]}
                                </p>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="space-y-3">
                        {/* Regular wishlist gifts */}
                        {regularGifts.map(gift => {
                          const isClaimed = gift.claimedBy !== null;
                          const claimedByMe = gift.claimedBy === currentUser;

                          return (
                            <div
                              key={gift.id}
                              data-fresh={freshIds.includes(gift.id)}
                              className={`p-3 border rounded-lg ${freshClass(gift.id)} ${
                                claimedByMe
                                  ? 'bg-blue-50 border-blue-300'
                                  : isClaimed
                                  ? 'bg-gray-100 border-gray-300'
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <div className="flex flex-col sm:flex-row items-start justify-between gap-2">
                                <div className="flex-1 min-w-0 w-full">
                                  <p className="font-medium text-gray-800 break-words text-sm sm:text-base">
                                    {gift.title}{newChip(gift.id)}
                                  </p>
                                  {safeHref(gift.link) && (
                                    <a
                                      href={safeHref(gift.link)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs sm:text-sm text-blue-500 hover:underline break-all block"
                                    >
                                      View link
                                    </a>
                                  )}
                                  {gift.notes && (
                                    <p className="text-xs sm:text-sm text-gray-600 mt-2 whitespace-pre-wrap break-words">
                                      {gift.notes}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="flex gap-1 w-full sm:w-auto justify-end sm:justify-start">
                                  {claimedByMe ? (
                                    <button
                                      onClick={() => toggleClaim(person, gift.id)}
                                      className="px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded hover:bg-blue-600 whitespace-nowrap"
                                    >
                                      Unclaim
                                    </button>
                                  ) : !isClaimed ? (
                                    <button
                                      onClick={() => toggleClaim(person, gift.id)}
                                      className="px-3 py-1 bg-green-500 text-white text-xs sm:text-sm rounded hover:bg-green-600 whitespace-nowrap"
                                    >
                                      Claim
                                    </button>
                                  ) : (
                                    <span className="px-3 py-1 bg-gray-200 text-gray-500 text-xs sm:text-sm rounded whitespace-nowrap">
                                      Claimed by {gift.claimedBy}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Custom gifts section - at the bottom */}
                        {customGifts.map(gift => {
                          const isEditing = editingGiftId === gift.id;
                          const isDeleting = showDeleteConfirm === gift.id;
                          const isGiftedConfirm = showGiftedConfirm === gift.id;
                          const isCreator = gift.addedBy === currentUser;

                          return (
                            <div
                              key={gift.id}
                              data-fresh={freshIds.includes(gift.id)}
                              className={`p-3 border rounded-lg bg-gray-100 border-gray-400 ${freshClass(gift.id)}`}
                            >
                              {isEditing && isCreator ? (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                    placeholder="Gift title"
                                  />
                                  <input
                                    type="text"
                                    value={editLink}
                                    onChange={(e) => { setEditLink(e.target.value); setLinkError(''); }}
                                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                    placeholder="Link (optional)"
                                  />
                                  {linkError && <p className="text-sm text-red-600">{linkError}</p>}
                                  <textarea
                                    value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    rows="2"
                                    className="w-full p-2 border border-gray-300 rounded-lg resize-none text-sm"
                                    placeholder="Description (optional)"
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => saveEdit(person)}
                                      className="flex-1 px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 flex items-center justify-center gap-1"
                                    >
                                      <Save className="w-4 h-4" />
                                      Save
                                    </button>
                                    <button
                                      onClick={cancelEdit}
                                      className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 flex items-center gap-1"
                                    >
                                      <X className="w-4 h-4" />
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : isGiftedConfirm && isCreator ? (
                                <div>
                                  <p className="font-medium text-gray-800 mb-3 text-sm">
                                    Mark this custom gift as given?
                                  </p>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => markAsGifted(person, gift.id)}
                                      className="flex-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                                    >
                                      Yes, I gave it
                                    </button>
                                    <button
                                      onClick={() => setShowGiftedConfirm(null)}
                                      className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : isDeleting && isCreator ? (
                                <div>
                                  <p className="font-medium text-gray-800 mb-3 text-sm">
                                    Delete this custom gift?
                                  </p>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => deleteGift(person, gift.id)}
                                      className="flex-1 px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                                    >
                                      Yes, Delete
                                    </button>
                                    <button
                                      onClick={() => setShowDeleteConfirm(null)}
                                      className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-700 break-words text-sm">
                                      {gift.title}{newChip(gift.id)}
                                    </p>
                                    {safeHref(gift.link) && (
                                      <a
                                        href={safeHref(gift.link)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs sm:text-sm text-blue-500 hover:underline break-all block"
                                      >
                                        View link
                                      </a>
                                    )}
                                    {gift.notes && (
                                      <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap break-words">
                                        {gift.notes}
                                      </p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1 italic">
                                      Added by {gift.addedBy}
                                    </p>
                                  </div>
                                  
                                  <div className="flex flex-col items-end gap-1">
                                  {gift.claimedBy === currentUser ? (
                                    <button
                                      onClick={() => toggleClaim(person, gift.id)}
                                      className="px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded hover:bg-blue-600 whitespace-nowrap"
                                    >
                                      Unclaim
                                    </button>
                                  ) : !gift.claimedBy ? (
                                    <button
                                      onClick={() => toggleClaim(person, gift.id)}
                                      className="px-3 py-1 bg-green-500 text-white text-xs sm:text-sm rounded hover:bg-green-600 whitespace-nowrap"
                                    >
                                      Claim
                                    </button>
                                  ) : (
                                    <span className="px-3 py-1 bg-gray-200 text-gray-500 text-xs sm:text-sm rounded whitespace-nowrap">
                                      Claimed by {gift.claimedBy}
                                    </span>
                                  )}
                                  {isCreator && (
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => startEdit(gift)}
                                        className="p-1 text-blue-500 hover:bg-blue-100 rounded"
                                        title="Edit"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setShowGiftedConfirm(gift.id)}
                                        className="p-1 text-green-500 hover:bg-green-100 rounded"
                                        title="Mark as Gifted"
                                      >
                                        <Gift className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setShowDeleteConfirm(gift.id)}
                                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Add custom gift button/form */}
                        {addingCustomGiftFor === person ? (
                          <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Add Custom Gift for {person}
                            </h4>
                            <div className="space-y-2">
                              <input
                                type="text"
                                placeholder="Gift title"
                                value={customGiftTitle}
                                onChange={(e) => setCustomGiftTitle(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                              />
                              <input
                                type="text"
                                placeholder="Link (optional)"
                                value={customGiftLink}
                                onChange={(e) => { setCustomGiftLink(e.target.value); setLinkError(''); }}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                              />
                              {linkError && addingCustomGiftFor === person && <p className="text-sm text-red-600">{linkError}</p>}
                              <textarea
                                placeholder="Description (optional)"
                                value={customGiftDescription}
                                onChange={(e) => setCustomGiftDescription(e.target.value)}
                                rows="2"
                                className="w-full p-2 border border-gray-300 rounded-lg resize-none text-sm"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => addCustomGift(person)}
                                  disabled={!customGiftTitle}
                                  className="flex-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => {
                                    setAddingCustomGiftFor(null);
                                    setCustomGiftTitle('');
                                    setCustomGiftDescription('');
                                    setCustomGiftLink('');
                                    setLinkError('');
                                  }}
                                  className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="smart-box">
                            <div className="smart-actions">
                              <button
                                onClick={() => setAddingCustomGiftFor(person)}
                                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors text-sm flex items-center justify-center gap-2"
                              >
                                <Plus className="w-4 h-4" />
                                Add Custom Gift for {person}
                              </button>
                              <button
                                onClick={() => setSmartAddFor(person)}
                                className="w-full px-3 py-2 border-2 border-dashed border-green-600 text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-colors text-sm flex items-center justify-center gap-2"
                              >
                                <Sparkles className="w-4 h-4" />
                                Smart Add
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {regularGifts.length === 0 && customGifts.length === 0 && (
                          <p className="text-gray-400 text-center py-4">
                            No gifts yet
                          </p>
                        )}
                      </div>
                    </div>
                  )})}
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>

      {smartAddFor && (
        <SmartAddSheet
          key={smartAddFor}
          person={smartAddFor}
          isOwnList={smartAddFor === currentUser}
          existingTitles={(gifts[smartAddFor] || [])
            .filter(g => g.status !== 'gifted' && (smartAddFor !== currentUser || !g.isCustomGift))
            .map(g => g.title)}
          onSave={(rows) => saveSmartAdd(smartAddFor, rows)}
          onClose={closeSmartAdd}
        />
      )}

      {toast && (
        <div role="status" aria-live="polite" className="fixed left-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white rounded-xl shadow-xl text-sm"
          style={{ transform: 'translateX(-50%)', bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', maxWidth: 'calc(100% - 32px)', width: 'max-content', padding: '10px 12px 10px 16px' }}>
          <span className="min-w-0 break-words">{toast.message}</span>
          {toast.undo && (
            <button onClick={() => { const u = toast.undo; setToast(null); undoSmartAdd(u); }} className="font-bold underline flex-shrink-0 px-2" style={{ minHeight: 40 }}>
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
}